import { memo, useCallback } from "react";

import { modules, useFeature } from "byzantine";
import { Runtime } from "@narmi/tile-runtime";
import App from "byzantine/src/App";

import { useAppLaunch } from "../appAuthorize";

type Props = {
  tile: API.NarmiTile;
};

const NarmiTile = ({ tile }: Props) => {
  const { redirectUri, refreshAuthorization } =
    modules.narmiTiles.useTile(tile);

  const nafApp = App.deserialize(tile);
  const { handleClick } = useAppLaunch(nafApp);

  const openFullView = useCallback(async () => {
    const hasFullView = tile.views.includes("full");
    if (hasFullView) {
      handleClick();
    }
    return hasFullView;
  }, [tile.views, handleClick]);

  const onVersionFail = useCallback(
    (tileVersion: string, parentVersion: string) => {
      window.Sentry?.captureMessage("Tile version incompatible with parent.", {
        level: "fatal",
        extra: {
          tileVersion,
          parentVersion,
          tile,
        },
        tags: {
          feature: "tiles",
        },
      });
    },
    [tile]
  );

  const onUnhandledMessage = useCallback(
    (rawData: unknown) => {
      window.Sentry?.captureMessage("Tile sent unknown message.", {
        level: "warning",
        extra: {
          rawData,
          tile,
        },
        tags: {
          feature: "tiles",
        },
      });
    },
    [tile]
  );

  return (
    <Runtime
      platform={"web"}
      redirectUri={redirectUri}
      refreshAuthorization={refreshAuthorization}
      onVersionFail={onVersionFail}
      onUnhandledMessage={onUnhandledMessage}
      openFullView={openFullView}
    />
  );
};

const NarmiTiles = () => {
  const tiles = modules.narmiTiles.useTiles();

  const { featureEnabled } = useFeature();

  if (!featureEnabled("tiles_web")) {
    return null;
  }

  return tiles.map((tile) => <NarmiTile tile={tile} key={tile.client_id} />);
};

export default memo(NarmiTiles);
